import React from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add, Schedule, Person } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showSnackbar } from "../../store/slices/uiSlice";
import StatCard from "../../components/shared/StatCard";

const trainingStats = [
  {
    label: "Enrolled (Month)",
    value: "87",
    icon: "School",
    delta: "+14 more",
    deltaType: "up" as const,
    color: "#85D1DB",
    iconBg: "rgba(133,209,219,0.1)",
  },
  {
    label: "Completion Rate",
    value: "74%",
    icon: "CheckCircle",
    delta: "+6% vs March",
    deltaType: "up" as const,
    color: "#16a34a",
    iconBg: "#dcfce7",
  },
  {
    label: "Active Programs",
    value: "5",
    icon: "PlayCircle",
    delta: "2 ending soon",
    deltaType: "neutral" as const,
    color: "#d97706",
    iconBg: "#fef3c7",
  },
  {
    label: "Avg Duration",
    value: "14.8h",
    icon: "Schedule",
    delta: "Per program",
    deltaType: "neutral" as const,
    color: "#6BB8C3",
    iconBg: "#C9FDF2",
  },
];

const CAT_COLORS: Record<string, { bg: string; text: string }> = {
  Technical: { bg: "rgba(133,209,219,0.1)", text: "#85D1DB" },
  "Soft Skills": { bg: "#ccfbf1", text: "#0d9488" },
  Sales: { bg: "#fef3c7", text: "#d97706" },
  Compliance: { bg: "#fee2e2", text: "#dc2626" },
  Leadership: { bg: "rgba(182,242,209,0.1)", text: "#B6F2D1" },
};

// Using global theme for table headers

const Training: React.FC = () => {
  const dispatch = useAppDispatch();
  const programs = useAppSelector((s) => s.data.training);

  const currentUser = useAppSelector((s) => s.auth.user);
  const currentEmpId = useAppSelector((s) => {
    const emp = s.data.employees.find(
      (e) => e.user?._id === currentUser?._id || e.user === currentUser?._id,
    );
    return emp ? emp._id || emp.id : currentUser?._id;
  });

  const getEnrollment = (p: any) =>
    p.enrolledEmployees?.find(
      (e: any) =>
        e.employee === currentEmpId || e.employee?._id === currentEmpId,
    );

  const pct = (p: any) => {
    const enrollment = getEnrollment(p);
    if (!enrollment) return 0;
    return Math.round(
      (enrollment.completedModules / (p.totalModules || 5)) * 100,
    );
  };

  const handleEnroll = async (programId: string, programName: string) => {
    try {
      const { enrollTrainingAsync } =
        await import("../../store/slices/dataSlice");
      await dispatch(enrollTrainingAsync(programId)).unwrap();
      dispatch(
        showSnackbar({
          message: `Successfully enrolled in "${programName}"`,
          severity: "success",
        }),
      );
    } catch (err: any) {
      dispatch(
        showSnackbar({ message: err || "Failed to enroll", severity: "error" }),
      );
    }
  };

  const handleCompleteModule = async (programId: string) => {
    try {
      const { updateTrainingProgressAsync } =
        await import("../../store/slices/dataSlice");
      await dispatch(updateTrainingProgressAsync(programId)).unwrap();
      dispatch(
        showSnackbar({ message: `Progress updated`, severity: "success" }),
      );
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err || "Failed to update progress",
          severity: "error",
        }),
      );
    }
  };

  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    title: "",
    category: "Technical",
    instructor: "",
    startDate: "",
    endDate: "",
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const handleCreate = async () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!form.title) {
      newErrors.title = "Program Title is required";
      isValid = false;
    } else if (form.title.trim().length < 3) {
      newErrors.title = "Min 3 characters";
      isValid = false;
    }

    if (!form.instructor) {
      newErrors.instructor = "Instructor name is required";
      isValid = false;
    } else if (form.instructor.trim().length < 3) {
      newErrors.instructor = "Min 3 characters";
      isValid = false;
    }

    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!form.startDate) {
      newErrors.startDate = "Start Date is required";
      isValid = false;
    } else if (start < today) {
      newErrors.startDate = "Cannot be in past";
      isValid = false;
    }

    if (!form.endDate) {
      newErrors.endDate = "End Date is required";
      isValid = false;
    } else if (end < start) {
      newErrors.endDate = "Cannot be before start date";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;
    // dynamically import from slice
    try {
      const { addTrainingProgramAsync } =
        await import("../../store/slices/dataSlice");
      await dispatch(addTrainingProgramAsync(form)).unwrap();
      dispatch(
        showSnackbar({ message: "New program created", severity: "success" }),
      );
      setOpen(false);
      setForm({
        title: "",
        category: "Technical",
        instructor: "",
        startDate: "",
        endDate: "",
      });
      setErrors({});
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err || "Failed to create program",
          severity: "error",
        }),
      );
    }
  };

  return (
    <Box>
      <SEO title="Training" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {trainingStats.map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 2.5, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            Training Programs
          </Typography>
          {currentUser &&
            ["admin", "hr", "manager"].includes(
              currentUser.role.toLowerCase(),
            ) && (
              <Button
                size="small"
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpen(true)}
                sx={{ fontSize: 12 }}
              >
                New Program
              </Button>
            )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Program",
                  "Category",
                  "Instructor",
                  "Enrolled",
                  "Completion",
                  "Duration",
                  "Deadline",
                  "Action",
                ].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.map((p) => (
                <TableRow
                  key={p.id}
                  sx={{
                    transition: "background 0.15s",
                    "&:hover": { background: "#fafbff" },
                  }}
                >
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {p.name}
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <Chip
                      label={p.category}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: "99px",
                        background: CAT_COLORS[p.category]?.bg || "#f1f5f9",
                        color: CAT_COLORS[p.category]?.text || "#475569",
                        "& .MuiChip-label": { px: 1.2 },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  >
                    {p.instructor}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                      fontFamily: "'JetBrains Mono',monospace",
                    }}
                  >
                    {p.enrolled}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      minWidth: 150,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct(p)}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 99,
                          backgroundColor: "#e2e8f0",
                          "& .MuiLinearProgress-bar": {
                            background: "#85D1DB",
                            borderRadius: 99,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: "#85D1DB",
                          minWidth: 32,
                        }}
                      >
                        {pct(p)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      fontFamily: "'JetBrains Mono',monospace",
                    }}
                  >
                    {p.duration}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      color: p.deadline.includes("Apr") ? "#dc2626" : "#64748b",
                      fontWeight: p.deadline.includes("Apr") ? 700 : 400,
                    }}
                  >
                    {p.deadline}
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    {(() => {
                      const enrollment = getEnrollment(p);
                      if (!enrollment) {
                        return (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEnroll(p.id, p.name)}
                            sx={{
                              fontSize: 11,
                              height: 28,
                              px: 1.2,
                              borderRadius: 1.5,
                            }}
                          >
                            Enroll
                          </Button>
                        );
                      }
                      if (pct(p) >= 100) {
                        return (
                          <Chip
                            label="Completed"
                            size="small"
                            color="success"
                            sx={{ height: 28, fontSize: 11 }}
                          />
                        );
                      }
                      return (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          onClick={() => handleCompleteModule(p.id)}
                          sx={{
                            fontSize: 11,
                            height: 28,
                            px: 1.2,
                            borderRadius: 1.5,
                          }}
                        >
                          Complete Module
                        </Button>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Grid container spacing={2}>
        {programs.map((p) => (
          <Grid item xs={12} sm={6} lg={4} key={p.id}>
            <Card
              sx={{
                p: 0,
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08) !important",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: "18px !important" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: CAT_COLORS[p.category]?.text || "#94a3b8",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: CAT_COLORS[p.category]?.text || "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {p.category}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    mb: 0.8,
                    lineHeight: 1.3,
                  }}
                >
                  {p.name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "#94a3b8",
                    fontSize: 12,
                    mb: 1.5,
                  }}
                >
                  <Person sx={{ fontSize: 14 }} /> {p.instructor} &nbsp;·&nbsp;{" "}
                  <Schedule sx={{ fontSize: 14 }} /> {p.duration}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    Completion
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontFamily: "'JetBrains Mono',monospace",
                      fontWeight: 700,
                      color: "#85D1DB",
                    }}
                  >
                    {pct(p)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={pct(p)}
                  sx={{
                    height: 6,
                    borderRadius: 99,
                    backgroundColor: "#e2e8f0",
                    mb: 0.5,
                    "& .MuiLinearProgress-bar": {
                      background: "#85D1DB",
                      borderRadius: 99,
                    },
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mb: 1.5 }}>
                  {getEnrollment(p)
                    ? `${getEnrollment(p)?.completedModules} of ${p.totalModules || 5} modules completed`
                    : "Not enrolled yet"}
                </Typography>
                <Box
                  sx={{
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: p.deadline.includes("Apr") ? "#dc2626" : "#64748b",
                      fontWeight: p.deadline.includes("Apr") ? 700 : 400,
                    }}
                  >
                    📅 {p.deadline}
                  </Typography>
                  {(() => {
                    const enrollment = getEnrollment(p);
                    if (!enrollment) {
                      return (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleEnroll(p.id, p.name)}
                          sx={{ fontSize: 11, height: 28, borderRadius: 1.5 }}
                        >
                          Enroll
                        </Button>
                      );
                    }
                    if (pct(p) >= 100) {
                      return (
                        <Chip
                          label="Completed"
                          size="small"
                          color="success"
                          sx={{ height: 28, fontSize: 11 }}
                        />
                      );
                    }
                    return (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => handleCompleteModule(p.id)}
                        sx={{ fontSize: 11, height: 28, borderRadius: 1.5 }}
                      >
                        + Module
                      </Button>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Create New Program
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Program Title"
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value });
                  if (errors.title) setErrors((err) => ({ ...err, title: "" }));
                }}
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category}
                  label="Category"
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                >
                  {Object.keys(CAT_COLORS).map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Instructor"
                value={form.instructor}
                onChange={(e) => {
                  setForm({ ...form, instructor: e.target.value });
                  if (errors.instructor)
                    setErrors((err) => ({ ...err, instructor: "" }));
                }}
                error={!!errors.instructor}
                helperText={errors.instructor}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.startDate}
                onChange={(e) => {
                  setForm({ ...form, startDate: e.target.value });
                  if (errors.startDate)
                    setErrors((err) => ({ ...err, startDate: "" }));
                }}
                error={!!errors.startDate}
                helperText={errors.startDate}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.endDate}
                onChange={(e) => {
                  setForm({ ...form, endDate: e.target.value });
                  if (errors.endDate)
                    setErrors((err) => ({ ...err, endDate: "" }));
                }}
                error={!!errors.endDate}
                helperText={errors.endDate}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disableElevation>
            Create Program
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Training;
